import { useState, useEffect } from 'react';
import { ModelSection, ModelCard } from '../components/models/types';
import { ApiModelsResponse, ApiModel } from '../components/models/apiTypes';
import api from '../utils/api';
import VideoImage from '../assets/Video.png';
import VideoImage1 from '../assets/Video2.png';
import VideoImage2 from '../assets/Video3.png';
import VideoImage3 from '../assets/Video4.png';
import VideoImage4 from '../assets/Video5.png';
import VideoImage5 from '../assets/Video6.png';

// Helper function to pick a placeholder image deterministically based on ID
const images = [VideoImage, VideoImage1, VideoImage2, VideoImage3, VideoImage4, VideoImage5];
const getPlaceholderImage = (index: number) => {
  return images[index % images.length];
};

// Cache for models data to prevent refetching on every navigation
let cachedSections: ModelSection[] | null = null;
let isFetching = false;

/**
 * Hook to fetch video models data from API
 */
export const useVideoModels = () => {
  const [sections, setSections] = useState<ModelSection[]>(cachedSections || []);
  const [isLoading, setIsLoading] = useState(!cachedSections);

  useEffect(() => {
    // If we already have cached data, use it immediately (no refetch needed)
    if (cachedSections) {
      setSections(cachedSections);
      setIsLoading(false);
      return;
    }

    // Prevent multiple simultaneous fetches
    if (isFetching) return;

    const fetchVideoModels = async () => {
      isFetching = true;
      setIsLoading(true);

      try {
        const response = await api.get<ApiModelsResponse>('/image-generation/v1/models?offset=0&size=100');
        // console.log('API Response for video models:', response.data);

        if (response.data && response.data.models) {
          const apiModels = response.data.models;

          // Define sections arrays
          let textToVideoModels: ModelCard[] = [];
          let imageToVideoModels: ModelCard[] = [];

          let imageIndex = 0;

          // Process API models
          // Show ALL models from API, grouped by modelType (no allowed list filtering)
          apiModels.forEach((model: ApiModel) => {
            // Only process video models
            if (model.modelType !== 'text-to-video' && model.modelType !== 'image-to-video') {
              return;
            }

            const modelCard: ModelCard = {
              id: model.id,
              title: model.name,
              description: model.description,
              image: model.previewImageUrl || getPlaceholderImage(imageIndex++),
              selected: false,
              apiModel: model // Store full API model data
            };

            // Add to appropriate section based on modelType
            if (model.modelType === 'text-to-video') {
              textToVideoModels.push(modelCard);
            } else if (model.modelType === 'image-to-video') {
              imageToVideoModels.push(modelCard);
            }
          });

          // Sort by API position field (maintains backend order)
          const sortByPosition = (models: ModelCard[]) => {
            return models.sort((a, b) => {
              const positionA = a.apiModel?.position ?? Number.MAX_SAFE_INTEGER;
              const positionB = b.apiModel?.position ?? Number.MAX_SAFE_INTEGER;
              return positionA - positionB;
            });
          };

          textToVideoModels = sortByPosition(textToVideoModels);
          imageToVideoModels = sortByPosition(imageToVideoModels);

          // Create sections
          const newSections: ModelSection[] = [];

          if (textToVideoModels.length > 0) {
            newSections.push({
              title: 'Text To Video',
              models: textToVideoModels
            });
          }

          if (imageToVideoModels.length > 0) {
            newSections.push({
              title: 'Image To Video',
              models: imageToVideoModels
            });
          }

          // Cache the results
          cachedSections = newSections;
          setSections(newSections);
        } else {
          // Fallback to empty sections if API fails
          cachedSections = [];
          setSections([]);
        }
      } catch (error) {
        console.error('Error fetching video models:', error);
        // Fallback to empty sections on error
        cachedSections = [];
        setSections([]);
      } finally {
        setIsLoading(false);
        isFetching = false;
      }
    };

    fetchVideoModels();
  }, []);

  return { sections, isLoading };
};

